/**
 * The sequence edit lock in the editor (0.97.0): this tab acquires it on open, keeps it with a heartbeat every 30 s
 * and lets go when the editor closes (with keepalive at pagehide). Without it the editor is read-only and checks
 * the lock every 10 s, follows the holder's saves, and offers Start editing once it is free.
 *
 * The holder's tab follows sequence.lock_* events: on a takeover it saves, lets go and turns read-only; on Ask to
 * edit it shows the request (Hand over or Dismiss). The asker's and the taker's tabs read the outcome on the lock.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, errorMessage } from '../../api/client';
import { onDomainEvent } from '../../api/events';
import { toast } from '../../components/Toasts';

/** This page's tab id: a new one for every page load, so two tabs never share it (a duplicated tab included). */
export const TAB_ID = (globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`).replace(/[^A-Za-z0-9_-]/g, '');
const TAB_HEADERS = { 'x-prism-tab': TAB_ID };
/**
 * The id of the page this browser tab showed before a reload: opening the editor again takes that page's lock
 * (its release at pagehide may arrive after the new page asks).
 */
const PREVIOUS_TAB = (() => {
  try {
    const prev = sessionStorage.getItem('prism.editor-tab');
    sessionStorage.setItem('prism.editor-tab', TAB_ID);
    return prev;
  } catch {
    return null;
  }
})();
/** A release still on its way (the editor closed and opened again in this page): the next acquire waits for it. */
let releasing: Promise<unknown> = Promise.resolve();
const HEARTBEAT_MS = 30_000;
const POLL_MS = 10_000;
const TAKEOVER_POLL_MS = 1000;
const TAKEOVER_WAIT_MS = 15_000;

export interface Person { id: string; displayName: string }
export interface LockState {
  revision: number;
  lock: { holder: Person; since: string; sameUser: boolean; mine: boolean } | null;
  takeover: { by: Person; at: string; kind: 'takeover' | 'here'; toMe: boolean } | null;
  request: { by: Person; at: string; mine: boolean; dismissed: boolean } | null;
}

/** What the editor shows: editing here, read-only (who holds it, or nobody), or waiting for a takeover. */
export type LockMode = 'starting' | 'editing' | 'readonly' | 'taking';

/** Why this tab is read-only after holding the lock: the notice it shows. */
export interface LostNotice { by: string; kind: 'takeover' | 'here' | 'handover' | 'lost'; unsaved: boolean }

export interface LockHooks {
  /** Save everything before letting go; resolves true when nothing is left unsaved. */
  settle: () => Promise<boolean>;
  /** Edits not saved yet. */
  isDirty: () => boolean;
  /** The editor's revision: a read-only editor reloads when the saved one is newer. */
  revision: () => number;
  /** Load the latest saved sequence into the editor (a read-only editor following saves, or before editing). */
  reload: () => Promise<void>;
}

const url = (id: string, path = '') => `/api/v1/sequences/${id}/lock${path}`;

export function useSequenceLock(id: string, name: string, canEdit: boolean, hooks: LockHooks) {
  const [mode, setModeRaw] = useState<LockMode>('starting');
  const [state, setState] = useState<LockState | null>(null);
  const [lost, setLost] = useState<LostNotice | null>(null);
  const [asked, setAskedRaw] = useState(false);
  const askedRef = useRef(false);
  const setAsked = (a: boolean) => { askedRef.current = a; setAskedRaw(a); };
  // The last holder seen, for "… handed over editing" (the lock no longer names them by then).
  const holderRef = useRef<string | null>(null);
  const modeRef = useRef<LockMode>('starting');
  const hooksRef = useRef(hooks);
  hooksRef.current = hooks;
  const nameRef = useRef(name);
  nameRef.current = name;
  const letting = useRef(false);
  const setMode = (m: LockMode) => { modeRef.current = m; setModeRaw(m); };

  /** Follow what the server says about the lock. */
  const follow = useCallback(async (s: LockState) => {
    setState(s);
    const previousHolder = holderRef.current;
    if (s.lock && !s.lock.mine) holderRef.current = s.lock.holder.displayName;
    const m = modeRef.current;
    if (m === 'editing') {
      if (!s.lock?.mine) {
        // Lost: moved after a takeover this tab did not answer in time, or expired.
        setMode('readonly');
        setLost({ by: s.lock?.holder.displayName ?? 'Someone', kind: 'lost', unsaved: hooksRef.current.isDirty() });
        return;
      }
      if (s.takeover && !s.takeover.toMe && !letting.current) {
        // Someone takes over (or this person continues elsewhere): save first, then let go and turn read-only.
        letting.current = true;
        const t = s.takeover;
        const saved = await hooksRef.current.settle();
        await api(url(id), { method: 'DELETE', headers: TAB_HEADERS }).catch(() => undefined);
        letting.current = false;
        setMode('readonly');
        setLost({ by: t.by.displayName, kind: t.kind, unsaved: !saved });
        void api<LockState>(url(id), { headers: TAB_HEADERS }).then(setState).catch(() => undefined);
      }
      return;
    }
    if (m === 'readonly' || m === 'starting') {
      if (s.lock?.mine) {
        // Handed over to this tab (Ask to edit), or it took over: start from the latest save.
        await hooksRef.current.reload();
        setMode('editing');
        setLost(null);
        if (askedRef.current) toast(`${previousHolder ?? 'The editor'} handed over editing`);
        setAsked(false);
        return;
      }
      if (s.revision > hooksRef.current.revision()) await hooksRef.current.reload();
      if (s.request?.mine && s.request.dismissed && askedRef.current) {
        toast(`${s.lock?.holder.displayName ?? 'The editor'} is still editing “${nameRef.current}”`, 'error');
        setAsked(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const refresh = useCallback(async () => {
    try {
      await follow(await api<LockState>(url(id), { headers: TAB_HEADERS }));
    } catch { /* offline: the next poll tries again */ }
  }, [id, follow]);

  /** Try to get the lock (opening the editor, or Start editing): editing here, or read-only with the holder. */
  const acquire = useCallback(async (opening: boolean) => {
    if (!canEdit) {
      setMode('readonly');
      await refresh();
      return;
    }
    try {
      await releasing;
      const s = await api<LockState>(url(id), { method: 'POST', headers: opening && PREVIOUS_TAB ? { ...TAB_HEADERS, 'x-prism-previous-tab': PREVIOUS_TAB } : TAB_HEADERS });
      if (!opening) await hooksRef.current.reload();
      setState(s);
      setMode('editing');
      setLost(null);
    } catch (err) {
      if ((err as ApiError).code !== 'SEQUENCE_LOCKED' && !opening) toast(errorMessage(err), 'error');
      setMode('readonly');
      await refresh();
    }
  }, [id, canEdit, refresh]);

  // Open: acquire. Close: save, then let go.
  useEffect(() => {
    void acquire(true);
    const release = () => fetch(url(id), { method: 'DELETE', headers: TAB_HEADERS, credentials: 'same-origin', keepalive: true }).catch(() => undefined);
    const onHide = () => { if (modeRef.current === 'editing') void release(); };
    // Back from the back/forward cache: the lock was let go at pagehide, so take it again.
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) void acquire(true); };
    window.addEventListener('pagehide', onHide);
    window.addEventListener('pageshow', onShow);
    return () => {
      window.removeEventListener('pagehide', onHide);
      window.removeEventListener('pageshow', onShow);
      if (modeRef.current === 'editing') releasing = hooksRef.current.settle().finally(release);
      modeRef.current = 'readonly';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Heartbeat while editing; the lock state every 10 s while read-only.
  useEffect(() => {
    if (mode === 'editing') {
      const t = window.setInterval(() => {
        api<LockState>(url(id, '/heartbeat'), { method: 'POST', headers: TAB_HEADERS }).then(follow).catch((err: ApiError) => {
          if (err.code === 'SEQUENCE_LOCKED') void refresh();
        });
      }, HEARTBEAT_MS);
      return () => window.clearInterval(t);
    }
    if (mode === 'readonly') {
      const t = window.setInterval(() => void refresh(), POLL_MS);
      return () => window.clearInterval(t);
    }
    return undefined;
  }, [mode, id, follow, refresh]);

  // Takeovers, requests and handovers arrive as events on the live stream.
  useEffect(() => onDomainEvent((e) => {
    if (e.aggregate_id === id && e.type.startsWith('sequence.lock')) void refresh();
  }), [id, refresh]);

  /** Take over, or Edit here instead: wait until the holder's tab has saved and let go (at most ~10 s). */
  const takeOver = useCallback(async () => {
    setMode('taking');
    try {
      const r = await api<LockState>(url(id, '/takeover'), { method: 'POST', headers: TAB_HEADERS });
      const deadline = Date.now() + TAKEOVER_WAIT_MS;
      let s: LockState | null = r.lock?.mine ? r : null;
      while (!s && Date.now() < deadline) {
        await new Promise((res) => setTimeout(res, TAKEOVER_POLL_MS));
        try {
          s = await api<LockState>(url(id), { method: 'POST', headers: TAB_HEADERS });
        } catch (err) {
          if ((err as ApiError).code !== 'SEQUENCE_LOCKED') throw err;
          if (!(err as ApiError).details?.takeoverPending) break;
        }
      }
      if (!s) throw new Error('The sequence could not be taken over. Try again.');
      await hooksRef.current.reload();
      setState(s);
      setMode('editing');
      setLost(null);
    } catch (err) {
      toast(errorMessage(err), 'error');
      setMode('readonly');
      await refresh();
    }
  }, [id, refresh]);

  const askToEdit = useCallback(async () => {
    try {
      const s = await api<LockState & { free?: boolean }>(url(id, '/request'), { method: 'POST', headers: TAB_HEADERS });
      if (s.free) { await acquire(false); return; }
      setAsked(true);
      setState(s);
      toast(`Asked ${s.lock?.holder.displayName ?? 'the editor'} to hand over “${nameRef.current}”`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }, [id, acquire]);

  /** The holder's answer to Ask to edit. Hand over saves first. */
  const answer = useCallback(async (a: 'handover' | 'dismiss') => {
    const asker = state?.request?.by.displayName ?? 'Someone';
    try {
      if (a === 'handover') {
        letting.current = true;
        await hooksRef.current.settle();
      }
      const s = await api<LockState>(url(id, '/answer'), { method: 'POST', headers: TAB_HEADERS, json: { answer: a } });
      setState(s);
      if (a === 'handover') {
        setMode('readonly');
        setLost({ by: asker, kind: 'handover', unsaved: hooksRef.current.isDirty() });
      }
    } catch (err) {
      toast(errorMessage(err), 'error');
      void refresh();
    } finally {
      letting.current = false;
    }
  }, [id, state, refresh]);

  /** A save was refused (423): someone else has the lock now. */
  const onLocked = useCallback((err: ApiError) => {
    if (modeRef.current !== 'editing') return;
    setMode('readonly');
    setLost({ by: (err.details?.holder as Person | undefined)?.displayName ?? 'Someone', kind: 'lost', unsaved: true });
    void refresh();
  }, [refresh]);

  return {
    mode, state, lost, asked, editing: mode === 'editing', tabId: TAB_ID,
    startEditing: () => acquire(false), takeOver, askToEdit, answer, onLocked, dismissLost: () => setLost(null),
  };
}
